import type { Meta, ScoreBreakdown } from '../types';

function band(score: number): 'low' | 'mid' | 'high' {
  if (score >= 55) return 'high';
  if (score >= 30) return 'mid';
  return 'low';
}

/** Compact score with a fill bar, for the table. */
export function ScoreCell({ score }: { score: ScoreBreakdown }) {
  const top = score.factors.find((f) => f.reason && f.points > 0);
  return (
    <div>
      <div className={`score-cell ${band(score.score)}`}>
        <span className="score-num">{Math.round(score.score)}</span>
        <span className="score-bar">
          <span style={{ width: `${Math.min(score.score, 100)}%` }} />
        </span>
      </div>
      {top?.reason && <div className="score-reason">{top.reason}</div>}
      {!top?.reason && score.adjustments[0] && <div className="score-reason">{score.adjustments[0].label}</div>}
    </div>
  );
}

/**
 * The full breakdown. Every point in the score traces back to a factor and a
 * weight here — the ranking is meant to be arguable, not oracular.
 */
export function WhyPanel({ score, meta }: { score: ScoreBreakdown; meta: Meta | null }) {
  const label = (key: string) => meta?.factors.find((f) => f.id === key)?.label ?? key;
  const max = Math.max(...score.factors.map((f) => f.points), 1);

  return (
    <div className="why">
      <div className="why-head">
        <span className="big">{Math.round(score.score)}</span>
        <span className="cap">
          priority score
          {score.pinned && ' · pinned to the top'}
          {score.snoozed && ' · snoozed'}
        </span>
      </div>
      <div className="why-rows">
        {score.factors.map((f) => (
          <div key={f.key} className={`why-row ${f.points < 0.5 ? 'zero' : ''}`}>
            <span className="name">{label(f.key)}</span>
            <span className="track">
              <span style={{ width: `${(f.points / max) * 100}%` }} />
            </span>
            <span className="pts">{f.points >= 0.5 ? `+${f.points.toFixed(0)}` : '—'}</span>
            {f.reason && <span className="why-reason">{f.reason}</span>}
          </div>
        ))}
      </div>
      {score.adjustments.map((a, i) => (
        <div className="why-adjust" key={i}>
          {a.label} — score {a.multiplier === 0 ? 'held at zero' : `reduced to ${Math.round(a.multiplier * 100)}%`}
        </div>
      ))}
    </div>
  );
}
